import * as React from "react";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {Link} from "react-router-dom";
import {getAmountWithDecimalDigits, setSessionStorage} from "../../../../../CommonFunctions";
import {
    AnnualPolicyPremiumTypeEnum,
    ICacheManager,
    MAIN_ANNUAL_PAYMENT_CARD,
    Party,
    PaymentCard,
    Policy,
    Types
} from "@ferry/journey-services";
import {FalconNavigatorHeader, Footer} from "@ferry/portal-core";
import {lazyInject} from "@athena-portal/core";
import {PanelDashed} from "../../../../../components/PanelDashed";
import {FailedPaymentMessage} from "../../../../../components/payments/FailedPaymentMessage";
import type {ICustomerPortalService} from "../../../../../services/ICustomerPortalService";
import {Types as CustomerPortalTypes} from "../../../../../Types";

interface IParams {
    policyNumber: string
    invoiceId: string
    [key: string]: string | undefined
}

interface IState {
    policy?: Policy;
    party?: Party;
    amountToPay: number;
}

@authorize
export class PayOverdueInstalmentPagePlain extends React.Component<IWithRouterProps<IParams>, IState> {
    @lazyInject(CustomerPortalTypes.CustomerPortalService)
    private readonly customerPortalService: ICustomerPortalService;

    @lazyInject(Types.CacheManager)
    private readonly cacheManager: ICacheManager;

    private readonly pluginTemplates = new PluginTemplates();

    constructor(props: IWithRouterProps<IParams>) {
        super(props);

        this.state = {
            amountToPay: 0,
        };

        this.handleOnClick = this.handleOnClick.bind(this);
    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Payment");

        setSessionStorage("isChargeBack", false);
        setSessionStorage("missedPaymentPolicyNumber", this.props.params.policyNumber);
        this.cacheManager.clearCache("getPolicyHistory");

        const policyNumber: string = this.props.params.policyNumber;

        this.cacheManager.clearCache("getPolicy".concat(policyNumber));
        this.customerPortalService
            .getPolicy(policyNumber)
            .subscribe((policy: Policy) => {
                    this.setState({
                        policy,
                        amountToPay: this.getAmountToPay(policy)
                    })
                }
            );
        this.customerPortalService
            .getProfile()
            .subscribe((party: Party) => {
                this.setState({ party });
            });
    }

    public render() {
        const dataLoaded = this.state.policy && this.state.party;
        return (
            <React.Fragment>
                <Overlay show={!dataLoaded}/>
                <FalconHeader/>
                {dataLoaded && this.renderPageContents()}
                <Footer/>
            </React.Fragment>
        );
    }

    private readonly getRedirectUrl = (): string => {
        return this.state.policy?.annualPolicyInfo.premiumType === AnnualPolicyPremiumTypeEnum.MONTHLY
            ? `/dashboard/monthlyPaymentHistory/${this.props.params.policyNumber}`
            : `/dashboard/annualPaymentHistory/${this.props.params.policyNumber}`;
    }

    private renderPageContents() {
        const isMonthly = this.state.policy?.annualPolicyInfo.premiumType === AnnualPolicyPremiumTypeEnum.MONTHLY;
        const title= isMonthly ? "Pay overdue instalment" : "Pay off my outstanding balance";

        return (
            <div className="plugin">
                {this.props.location?.state?.verifyCardFailed &&
                    <FailedPaymentMessage/>}
                <FalconNavigatorHeader back={this.getRedirectUrl()} title={title}/>
                <div className="panel">
                    <div className="currency-amount-display price font-family-display font-weight-bold text-center">
                        {getAmountWithDecimalDigits(this.state.amountToPay)}
                    </div>
                    <div>
                        <p className="text-center font-weight-bold mb-0">{isMonthly ? "to pay for overdue instalment" : "to pay for outstanding balance"}</p>
                        <p className="text-center pt-2">All figures include Insurance Premium Tax</p>
                    </div>
                </div>
                <PanelDashed className="shadow bg-white text-center pay-by-card-panel">
                    <div className="panel-dashed-contents card-information">{this.renderCardInformation()}</div>
                    <div className="pb-2 or-divider"> OR</div>
                    <Link id="payNow" className="btn btn-white-dashboard mt-0 mb-0"
                          aria-label={"Pay Now"}
                          to={{
                              pathname: `/dashboard/account/missedPolicyPayment/${this.state.policy?.policyNumber}/${this.props.params.invoiceId ?? ""}`,
                          }} state={{
                                verifyCardFailed: false
                            }}>
                        Use a different card
                    </Link>
                    <div className="panel-dashed-contents card-buttons">{this.renderCardsButtons()}</div>
                </PanelDashed>
                <button type="button"
                        className="btn btn-default btn-white-dashboard m-auto"
                        onClick={this.navigateTo(this.getRedirectUrl())}>
                    Cancel changes
                </button>
                {this.pluginTemplates.renderBackReloadLink("Back", true, this.getRedirectUrl())}
            </div>
        );
    }

    private readonly renderCardInformation = () => {
        const card: PaymentCard | null = this.findMainPaymentCard();
        if (card) {
            return (
                <div className="d-flex justify-content-center mt-3">
                    <div className="card-details-wrapper">
                        <span className="pr-2">{this.renderCardImage(card)}</span>&nbsp;
                        <span>{this.renderCardTypeName(card)}</span>&nbsp;
                        <span>ending in <b>{card.cardNumberObfuscated.split('******')[1]}</b></span>
                    </div>
                </div>
            );
        } else {
            return <></>;
        }
    };

    private readonly renderCardImage = (card: PaymentCard) => {
        if (card.cardType.includes("VISA")) {
            return <img className="payment-icon" alt="Visa icon" src="/img/visa-icon.svg"/>;
        }
        if (card.cardType.includes("MASTERCARD")) {
            return <img className="payment-icon" alt="Mastercard icon" src="/img/mastercard-icon.svg"/>;
        }
        return <></>;
    };

    private readonly renderCardTypeName = (card: PaymentCard): string => {
        if (card.cardType.includes("VISA")) {
            return "Visa";
        }
        if (card.cardType.includes("MASTERCARD")) {
            return "Mastercard";
        }
        return "";
    };

    private readonly isMainPaymentCard = (card: PaymentCard): boolean => {
        const party = this.state.party;
        if (!party?.customProperties) {
            return false;
        }

        for (const property of party.customProperties) {
            if (property.key === MAIN_ANNUAL_PAYMENT_CARD && Number(property.value) === card.id) {
                return true;
            }
        }
        return false;
    }

    private findMainPaymentCard(): PaymentCard | null {
        const party = this.state.party;

        if (!party?.paymentCards?.length) {
            return null;
        }

        for (const card of party.paymentCards) {
            if (this.isMainPaymentCard(card)) {
                return card;
            }
        }
        return party.paymentCards[party.paymentCards.length - 1];
    }

    private renderPayButton(card: PaymentCard) {
        return (
            <button key={card.id}
                    className="btn btn-primary btn-make-payment m-auto margin-top-17 justify-content-center"
                    onClick={this.handleOnClick}
                    id={"card-" + card.cardNumberObfuscated}
                    aria-label={`${getAmountWithDecimalDigits(this.state.amountToPay)} to pay`}
            >
                {`Pay ${getAmountWithDecimalDigits(this.state.amountToPay)}`}
                <span className="payment-lock-image"/>
            </button>
        );
    }

    private readonly renderCardsButtons = () => {
        const card: PaymentCard | null = this.findMainPaymentCard();

        if (card) {
            return this.renderPayButton(card);
        }
        return null;
    };

    private handleOnClick(event: any): void {
        window.sessionStorage.setItem("paymentAmount", getAmountWithDecimalDigits(this.state.amountToPay).toString());
        window.location.assign(`/dashboard/account/paymentProcessing/${null}/${this.state.policy?.policyNumber}${this.props.params.invoiceId ? "/" + this.props.params.invoiceId : ""}`);
        event.preventDefault();
    }

    private navigateTo(link: string) {
        return () => {
            window.location.href = link;
        }
    }

    private readonly getAmountToPay = (policy: Policy): number => {
        const annualInfo = policy?.annualPolicyInfo;
        if (this.props.params.invoiceId) {
            return annualInfo?.missedAnnualPaymentInfo?.invoiceId?.toString() === this.props.params.invoiceId && annualInfo.missedAnnualPaymentInfo?.amount ?
                annualInfo.missedAnnualPaymentInfo.amount :
                annualInfo?.unpaidInstallmentIdToAmount[this.props.params.invoiceId];
        } else {
            return annualInfo?.overdueMonthlyInvoiceAmount || annualInfo?.overdueAnnualInvoiceAmount || 0;
        }
    }
}

export const PayOverdueInstalmentPage = withRouter<{}, IParams>(PayOverdueInstalmentPagePlain)
